import { API_BASE_URL } from '@src/runtimeConfig'
import { openMedia, fetchMediaBlob, isApiMediaUrl, safeTypedBlob } from '@src/services/media'
import React from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { File02 } from '@untitled-ui/icons-react'
import { MyConfirmModalWithChildren } from '@interstellar-component'
import { format } from 'date-fns'

const MONTHS_ID = [
  'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
  'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des',
]

function formatDob(dateStr) {
  if (!dateStr) return null
  try {
    return format(new Date(dateStr), 'd MMMM yyyy')
  } catch {
    return null
  }
}

// "YYYY-MM" (from MonthPicker) → "Agt 2024"
function formatMonth(ym) {
  if (!ym) return null
  const [y, m] = String(ym).split('-').map(Number)
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null
  return `${MONTHS_ID[m - 1]} ${y}`
}

function monthRange(start, end, isCurrent) {
  const s = formatMonth(start)
  if (!s) return ''
  const e = isCurrent ? 'sekarang' : formatMonth(end)
  return ` (${s}${e ? ` – ${e}` : ''})`
}

// AUTO mode: system-generated letter from enquiry/KTP-derived data.
function buildLetterContent(data) {
  const companyName = data?.companyName || '[Nama Perusahaan]'
  const fullName = data?.fullName || '[Nama Lengkap]'
  const birthPlace = data?.birthPlace || null
  const dob = formatDob(data?.dateOfBirth)
  const placeAndDob =
    birthPlace && dob
      ? `${birthPlace}, ${dob}`
      : dob || birthPlace || '[Tempat, Tanggal Lahir]'
  const email = data?.email || '[Alamat Email]'

  return `
    <p>Kepada Yth.<br>HRD / Bagian Sumber Daya Manusia<br><strong>${companyName}</strong><br>di Tempat</p>
    <p>Dengan hormat,<br>Saya yang bertanda tangan di bawah ini:</p>
    <p>Nama lengkap : <strong>${fullName}</strong><br>Tempat, tanggal lahir : <strong>${placeAndDob}</strong><br>Email : <strong>${email}</strong></p>
    <p>Dengan ini mengajukan lamaran pekerjaan di <strong>${companyName}</strong>. Saya bersedia mengikuti seluruh proses seleksi sesuai dengan ketentuan yang berlaku.</p>
    <p>Demikian surat lamaran ini saya sampaikan. Atas perhatian Bapak/Ibu, saya ucapkan terima kasih.</p>
    <p>Hormat saya,<br><strong>${fullName}</strong></p>
  `
}

// CANDIDATE mode: letter generated from what the candidate actually filled in
// the consent job form (payload) — richer than the auto letter (education +
// experience). Falls back to buildLetterContent when there's no payload yet.
function buildLetterFromPayload(payload, data) {
  if (!payload) return buildLetterContent(data)
  const companyName = data?.companyName || '[Nama Perusahaan]'
  const fullName = payload.full_name || data?.fullName || '[Nama Lengkap]'
  const email = payload.email || data?.email || '[Alamat Email]'
  const whatsapp = payload.whatsapp || null

  const edu = payload.education || {}
  const eduName = [edu.level, edu.school_name].filter(Boolean).join(' ').trim()
  const eduLine = eduName ? `${eduName}${monthRange(edu.start_month, edu.end_month)}` : null

  const experiences = Array.isArray(payload.experiences)
    ? payload.experiences.filter((e) => e && (e.company || e.position))
    : []
  const expItems = experiences
    .map((e) => {
      const role = [e.position, e.company].filter(Boolean).join(' — ') || '[Pengalaman]'
      return `<li>${role}${monthRange(e.start_month, e.end_month, e.is_current)}</li>`
    })
    .join('')

  return `
    <p>Kepada Yth.<br>HRD / Bagian Sumber Daya Manusia<br><strong>${companyName}</strong><br>di Tempat</p>
    <p>Dengan hormat,<br>Saya yang bertanda tangan di bawah ini:</p>
    <p>Nama lengkap : <strong>${fullName}</strong><br>Email : <strong>${email}</strong>${
      whatsapp ? `<br>WhatsApp : <strong>${whatsapp}</strong>` : ''
    }</p>
    ${eduLine ? `<p>Pendidikan terakhir:<br><strong>${eduLine}</strong></p>` : ''}
    ${expItems ? `<p>Pengalaman kerja:</p><ul>${expItems}</ul>` : ''}
    <p>Dengan ini mengajukan lamaran pekerjaan di <strong>${companyName}</strong>. Saya bersedia mengikuti seluruh proses seleksi sesuai dengan ketentuan yang berlaku.</p>
    <p>Demikian surat lamaran ini saya sampaikan. Atas perhatian Bapak/Ibu, saya ucapkan terima kasih.</p>
    <p>Hormat saya,<br><strong>${fullName}</strong></p>
  `
}

// enquiry_documents.file_path is a root-relative "/backoffice/uploads/<id>";
// the API base already ends in "/backoffice", so resolve against its origin.
function resolveDocUrl(filePath) {
  if (!filePath) return null
  if (/^https?:\/\//i.test(filePath)) return filePath
  const origin = (API_BASE_URL || '').replace(/\/backoffice\/?$/, '')
  return `${origin}${filePath.startsWith('/') ? '' : '/'}${filePath}`
}

// Renders the "Surat Lamaran Kerja" per job_application_form_mode:
//   - hr        → the HR-uploaded file (PDF, inline)
//   - candidate → a letter generated from the candidate's submitted form
//   - auto      → the system-generated letter
// `mode` comes from GET /report-enquiry/:id/job-application; while that request
// is in flight `mode` is undefined and we fall back to the auto letter.
export default function JobApplicationLetterModal({
  open,
  onClose,
  mode,
  content,
  data,
  payload,
  letterContent,
  document: doc,
  zIndex = 3000,
}) {
  const isHrFile = mode === 'hr' && doc?.file_path
  const docUrl = isHrFile ? resolveDocUrl(doc.file_path) : null
  // services/media refuses (no request, no token) a file off the API origin, so
  // don't offer an iframe/"open" that can only fail — say it's unavailable.
  const docAllowed = Boolean(docUrl) && isApiMediaUrl(docUrl)

  // The /uploads proxy needs an auth header, which an <iframe src=proxyUrl>
  // can't send — fetch the file as an authed blob and point the iframe there.
  // The HR letter is a PDF (Word files are converted on upload): the blob is
  // typed as one, so a file stored under another extension (an .svg, served
  // as image/svg+xml) runs no script in this same-origin iframe.
  const [docBlobUrl, setDocBlobUrl] = React.useState(null)
  React.useEffect(() => {
    if (!open || !docUrl) {
      setDocBlobUrl(null)
      return undefined
    }
    let active = true
    let obj = null
    const controller = new AbortController()
    fetchMediaBlob(docUrl, controller.signal)
      .then((blob) => (blob ? URL.createObjectURL(safeTypedBlob(blob, 'application/pdf')) : null))
      .then((u) => {
        if (!active) {
          if (u) URL.revokeObjectURL(u)
          return
        }
        obj = u
        setDocBlobUrl(u)
      })
      .catch(() => {
        if (active) setDocBlobUrl(null)
      })
    return () => {
      active = false
      controller.abort()
      if (obj) URL.revokeObjectURL(obj)
    }
  }, [open, docUrl])

  const resolvedContent =
    content ||
    (mode === 'candidate'
      ? letterContent || buildLetterFromPayload(payload, data)
      : buildLetterContent(data))

  const editor = useEditor(
    {
      extensions: [StarterKit],
      content: resolvedContent,
      editable: false,
      editorProps: {
        attributes: {
          class:
            'prose prose-sm max-w-none focus:outline-none text-[#535862] leading-relaxed',
        },
      },
    },
    [resolvedContent, open, isHrFile]
  )

  React.useEffect(() => {
    if (editor && open && !isHrFile) {
      editor.commands.setContent(resolvedContent)
    }
  }, [editor, open, resolvedContent, isHrFile])

  return (
    <MyConfirmModalWithChildren
      open={open}
      onClose={onClose}
      zIndex={zIndex}
      forceBlur
      title="JOB APPLICATION LETTER"
      icon={<File02 className="text-brand/900" />}
      bgColor="bg-white"
      disableBgPattern
      enableDoubleRing
      negativeActionWord={null}
      positiveActionWord="Close"
      onConfirm={onClose}
    >
      {isHrFile ? (
        <div className="min-h-[300px] py-2">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm text-[#535862] truncate">
              {doc.file_name || 'Application document'}
            </p>
            {docAllowed && (
              <button
                type="button"
                onClick={() => openMedia(docUrl, { type: 'application/pdf' }).catch(() => {})}
                className="shrink-0 text-sm font-semibold text-brand/700 hover:underline"
              >
                Open in new tab
              </button>
            )}
          </div>
          {docAllowed ? (
            <iframe
              src={docBlobUrl || undefined}
              title="Job application letter"
              className="h-[60vh] w-full rounded-lg border border-gray-200"
            />
          ) : (
            <p className="text-sm text-[#535862]">Document not available.</p>
          )}
        </div>
      ) : (
        <div className="min-h-[300px] py-2">
          <EditorContent editor={editor} />
        </div>
      )}

      <style>{`
        .prose strong {
          color: #181d27;
          font-weight: 600;
        }
        .prose p {
          margin-bottom: 1rem;
        }
        .prose ul {
          margin-bottom: 1rem;
          padding-left: 1.25rem;
          list-style: disc;
        }
      `}</style>
    </MyConfirmModalWithChildren>
  )
}
